import { getMaxTravelDate, jsonValueSchema } from '@massilia/contracts';
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { getEstimateFormSchema } from '../schemas/estimate/estimate-form.schema.js';
import { EMPTY_FLIGHT_LEG } from '../schemas/estimate/flight-schemas.js';
import { ESTIMATE_FORM_DEFAULT_VALUES, } from '../schemas/estimate/index.js';
import { useAppForm } from './use-app-form.js';
export function useEstimateForm({ defaultValues, enforceTravelHorizon = false, onSubmit, }) {
    return useAppForm({
        onSubmitInvalid: ({ formApi }) => {
            const allErrors = formApi.getAllErrors();
            const message = extractFirstErrorMessage(allErrors) ?? 'Veuillez vérifier les champs du formulaire';
            // eslint-disable-next-line no-console
            console.warn('[useEstimateForm] submit blocked by validation', allErrors);
            toast.error('❌ Formulaire invalide', { description: message });
        },
        defaultValues: defaultValues ?? ESTIMATE_FORM_DEFAULT_VALUES,
        listeners: {
            onChange: ({ fieldApi, formApi }) => {
                const currentFlights = formApi.getFieldValue('flights');
                if (formApi.getFieldValue('type') === 'ROUND_TRIP' &&
                    ['flights[0].departure', 'flights[0].destination'].includes(fieldApi.name)) {
                    formApi.setFieldValue('flights[1].departure', currentFlights[0].destination);
                    formApi.setFieldValue('flights[1].destination', currentFlights[0].departure);
                }
                if (fieldApi.name !== 'type')
                    return;
                const newType = fieldApi.state.value;
                switch (newType) {
                    // Types de séjour (TOURISM) : ce listener ne concerne que les tronçons de vol.
                    case 'CRUISE':
                    case 'STAY':
                    case 'TOUR': {
                        break;
                    }
                    case 'MULTI_DESTINATION': {
                        formApi.setFieldValue('flights', [
                            currentFlights[0] ?? { ...EMPTY_FLIGHT_LEG },
                            { ...EMPTY_FLIGHT_LEG },
                        ]);
                        break;
                    }
                    case 'ONE_WAY': {
                        formApi.setFieldValue('flights', [currentFlights[0] ?? { ...EMPTY_FLIGHT_LEG }]);
                        break;
                    }
                    case 'ROUND_TRIP': {
                        formApi.setFieldValue('flights', [
                            currentFlights[0] ?? { ...EMPTY_FLIGHT_LEG },
                            currentFlights[1] ?? { ...EMPTY_FLIGHT_LEG },
                        ]);
                        break;
                    }
                }
            },
        },
        onSubmit: async ({ value }) => {
            await onSubmit(value);
        },
        validationLogic: revalidateLogic({
            mode: 'submit',
            modeAfterSubmission: 'change',
        }),
        validators: {
            onDynamic: ({ value }) => {
                return standardSchemaValidators.validate({ validationSource: 'form', value }, getEstimateFormSchema(value.type, {
                    maxTravelDate: enforceTravelHorizon ? getMaxTravelDate() : undefined,
                }));
            },
        },
    });
}
// L'arbre d'erreurs de TanStack Form n'a pas de forme stable : selon le
// validateur, un nœud est une chaîne, un `{ message }`, un tableau ou un objet
// de sous-champs. On décode chaque forme connue plutôt que de sonder la
// représentation d'un nœud à la volée.
const errorNodeSchema = z.union([
    z.string().transform((message) => ({ kind: 'message', message })),
    z
        .object({ message: z.string().min(1) })
        .transform(({ message }) => ({ kind: 'message', message })),
    z.array(jsonValueSchema).transform((children) => ({ children, kind: 'children' })),
    z
        .record(z.string(), jsonValueSchema)
        .transform((node) => ({ children: Object.values(node), kind: 'children' })),
]);
function extractFirstErrorMessage(errors) {
    if (!errors)
        return null;
    const node = errorNodeSchema.safeParse(errors);
    if (!node.success)
        return null;
    if (node.data.kind === 'message')
        return node.data.message;
    for (const child of node.data.children) {
        const message = extractFirstErrorMessage(child);
        if (message)
            return message;
    }
    return null;
}
